// src/AuthContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { generateSecretKey, getPublicKey } from 'nostr-tools/pure';
import { bytesToHex, hexToBytes } from '@noble/hashes/utils';
import { isValidPrivateKeyFormat, clearAllSecureData } from './utils/secureStorage';
import { sanitizeMetadata } from './utils/sanitize';

const AuthContext = createContext();

// Storage key constants
const PRIVATE_KEY_STORAGE = 'govstr_pk';

export const AuthProvider = ({ children }) => {
  const [privateKey, setPrivateKey] = useState(null);
  const [publicKey, setPublicKey] = useState(null);
  const [userMetadata, setUserMetadata] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize from storage on mount
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const savedPrivateKey = localStorage.getItem(PRIVATE_KEY_STORAGE);
        
        if (savedPrivateKey && isValidPrivateKeyFormat(savedPrivateKey)) {
          const secretKeyBytes = hexToBytes(savedPrivateKey);
          const pubKey = getPublicKey(secretKeyBytes);
          setPrivateKey(savedPrivateKey);
          setPublicKey(pubKey);
        } else if (savedPrivateKey) {
          // Invalid key format - clear it
          console.warn('Invalid private key format found, clearing');
          localStorage.removeItem(PRIVATE_KEY_STORAGE);
        }
      } catch (error) {
        console.error('Failed to restore saved key:', error);
        localStorage.removeItem(PRIVATE_KEY_STORAGE);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Login with existing private key
  const login = useCallback((privateKeyHex) => {
    try {
      // Validate private key format
      const cleanKey = privateKeyHex.trim().toLowerCase();
      
      if (!isValidPrivateKeyFormat(cleanKey)) {
        console.error('Invalid private key format');
        return false;
      }
      
      const secretKeyBytes = hexToBytes(cleanKey);
      const pubKey = getPublicKey(secretKeyBytes);
      
      setPrivateKey(cleanKey);
      setPublicKey(pubKey);
      
      // Store securely
      localStorage.setItem(PRIVATE_KEY_STORAGE, cleanKey);
      
      return true;
    } catch (error) {
      console.error('Invalid private key:', error);
      return false;
    }
  }, []);

  // Create a new account with a fresh keypair
  const createAccount = useCallback(() => {
    try {
      const secretKeyBytes = generateSecretKey();
      const privKey = bytesToHex(secretKeyBytes);
      const pubKey = getPublicKey(secretKeyBytes);
      
      setPrivateKey(privKey);
      setPublicKey(pubKey);
      
      // Store securely
      localStorage.setItem(PRIVATE_KEY_STORAGE, privKey);
      
      return { privateKey: privKey, publicKey: pubKey };
    } catch (error) {
      console.error('Failed to create account:', error);
      return null;
    }
  }, []);

  // Logout and clear all sensitive data
  const logout = useCallback(() => {
    setPrivateKey(null);
    setPublicKey(null);
    setUserMetadata(null);
    
    // Clear all sensitive data from storage
    clearAllSecureData();
    localStorage.removeItem(PRIVATE_KEY_STORAGE);
    
    // Also clear session storage if any
    sessionStorage.clear();
  }, []);

  // Update user metadata with sanitization
  const updateUserMetadata = useCallback((metadata) => {
    const sanitized = sanitizeMetadata(metadata);
    setUserMetadata(sanitized);
  }, []);

  // Get the private key bytes for signing (don't expose raw key in context value)
  const getPrivateKeyBytes = useCallback(() => {
    if (!privateKey) return null;
    try {
      return hexToBytes(privateKey);
    } catch {
      return null;
    }
  }, [privateKey]);

  return (
    <AuthContext.Provider 
      value={{ 
        privateKey,
        publicKey,
        userMetadata,
        isLoading,
        login,
        logout,
        createAccount,
        updateUserMetadata,
        getPrivateKeyBytes,
        isAuthenticated: !!publicKey
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
